import * as React from "react";
import { cn } from "@/lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle01Icon } from "@hugeicons/core-free-icons";

interface GlowyCardProps {
  children: React.ReactNode;
  className?: string;
}

export function GlowyCard({ children, className }: GlowyCardProps) {
  return (
    <div
      className={cn(
        "relative hover:bg-white/[0.04] transition-all duration-300 group rounded-2xl pt-6 pr-6 pb-6 pl-6 glowy-card",
        className
      )}
    >
      {/* Rotating border effect */}
      <div className="glowy-card-border">
        <div className="glowy-card-border-inner"></div>
      </div>

      {children}
    </div>
  );
}

interface GlowyCardHeaderProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  className?: string;
}

export function GlowyCardHeader({
  icon,
  title,
  subtitle,
  action,
  className,
}: GlowyCardHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between mb-6", className)}>
      <div className="flex items-center gap-3">
        {icon && (
          <div className="h-10 w-10 rounded-xl border border-white/10 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center">
            {icon}
          </div>
        )}
        <div>
          <h3 className="text-xl font-medium tracking-tight text-white text-balance">{title}</h3>
          {subtitle && <p className="text-sm text-gray-300 font-medium text-balance">{subtitle}</p>}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

interface GlowyCardPriceProps {
  amount: string;
  period?: string;
  description?: string;
  className?: string;
}

export function GlowyCardPrice({
  amount,
  period,
  description,
  className,
}: GlowyCardPriceProps) {
  return (
    <div className={cn("mb-6", className)}>
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-semibold tracking-tight text-white">{amount}</span>
        {period && <span className="text-sm text-neutral-400">{period}</span>}
      </div>
      {description && <p className="text-sm text-gray-300 font-medium mt-2 text-balance">{description}</p>}
    </div>
  );
}

interface GlowyCardFeaturesProps {
  features: string[];
  className?: string;
}

export function GlowyCardFeatures({ features, className }: GlowyCardFeaturesProps) {
  return (
    <ul className={cn("space-y-3 text-sm text-neutral-300", className)}>
      {features.map((feature, index) => (
        <li key={index} className="flex items-start gap-3">
          <div className="glowy-card-check">
            <HugeiconsIcon icon={CheckmarkCircle01Icon} size={12} strokeWidth={2} />
          </div>
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}
